/**
 * File Upload Manager mit Drag & Drop
 */

class FileUploadManager {
    constructor(containerSelector, options = {}) {
        this.container = document.querySelector(containerSelector);

        if (!this.container) return;

        this.options = {
            maxFileSize: options.maxFileSize || 10 * 1024 * 1024, // 10MB
            allowedTypes: options.allowedTypes || ['image/*', 'application/pdf'],
            maxFiles: options.maxFiles || 5,
            onFilesAdded: options.onFilesAdded || null,
        };

        this.files = [];
        this.init();
    }

    init() {
        this.setupDragAndDrop();
        this.setupFileInput();
    }

    setupDragAndDrop() {
        const zone = this.container.querySelector('.file-upload-zone');
        if (!zone) return;

        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
            zone.addEventListener(eventName, (e) => this.preventDefaults(e), false);
        });

        ['dragenter', 'dragover'].forEach((eventName) => {
            zone.addEventListener(eventName, () => zone.classList.add('is-dragover'), false);
        });

        ['dragleave', 'drop'].forEach((eventName) => {
            zone.addEventListener(eventName, () => zone.classList.remove('is-dragover'), false);
        });

        zone.addEventListener('drop', (e) => this.handleDrop(e), false);
        zone.addEventListener('click', () => this.triggerFileInput(), false);
    }

  setupFileInput() {
    const input = this.container.querySelector('.file-upload-input');
    if (!input) return;

    this.isMultiple = input.hasAttribute('multiple');
    input.addEventListener('change', (e) => this.handleFileInput(e));
  }

    preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    triggerFileInput() {
        const input = this.container.querySelector('.file-upload-input');
        if (input) input.click();
    }

    handleFileInput(e) {
        const files = e.target.files;
        this.handleFiles(files);
    }

    handleDrop(e) {
        const files = e.dataTransfer.files;
        this.handleFiles(files);
    }

    handleFiles(fileList) {
      const newFiles = Array.from(fileList);

      if (!this.isMultiple) {
        const existingFileIds = this.files.map(f => f.id);
        existingFileIds.forEach(id => this.removeFile(id));
        this.files = [];
      }

      // Prüfe Dateianzahl
      if (this.files.length + newFiles.length > this.options.maxFiles) {
        this.showError(`Maximal ${this.options.maxFiles} Dateien erlaubt.`);
        return;
      }

      newFiles.forEach((file) => {
        if (this.validateFile(file)) {
          this.addFile(file);
        }
      });

      if (this.options.onFilesAdded) {
        this.options.onFilesAdded(this.files);
      }
    }

    validateFile(file) {
        // Größe
        if (file.size > this.options.maxFileSize) {
            this.showError(`${file.name} ist zu groß (Max: ${this.formatFileSize(this.options.maxFileSize)})`);
            return false;
        }

        // Dateityp
        const isAllowed = this.options.allowedTypes.some((type) => {
            if (type.includes('*')) {
                const typePrefix = type.split('/')[0];
                return file.type.startsWith(typePrefix);
            }
            return file.type === type;
        });

        if (!isAllowed) {
            this.showError(`${file.name} ist ein nicht unterstützter Dateityp.`);
            return false;
        }

        return true;
    }

    addFile(file) {
        const fileObj = {
            id: Date.now() + Math.random(),
            file: file,
            progress: 0,
            status: 'pending',
        };

        this.files.push(fileObj);
        this.renderFileItem(fileObj);
    }

    renderFileItem(fileObj) {
        let list = this.container.querySelector('.file-upload-list');

        if (!list) {
            list = document.createElement('ul');
            list.className = 'file-upload-list';
            const zone = this.container.querySelector('.file-upload-zone');
            zone.parentNode.insertBefore(list, zone.nextSibling);
        }

        const item = document.createElement('li');
        item.className = 'file-upload-item';
        item.dataset.fileId = fileObj.id;

        const icon = this.getFileIcon(fileObj.file.type);

        item.innerHTML = `
      <div class="file-upload-item-info">
        <div class="file-upload-item-icon">${icon}</div>
        <div>
          <div class="file-upload-name">${fileObj.file.name}</div>
          <span class="file-upload-size">${this.formatFileSize(fileObj.file.size)}</span>
        </div>
      </div>
      <button type="button" class="file-upload-remove" aria-label="Datei entfernen">×</button>
    `;

        const removeBtn = item.querySelector('.file-upload-remove');
        removeBtn.addEventListener('click', () => this.removeFile(fileObj.id));

        list.appendChild(item);
    }

    removeFile(fileId) {
        this.files = this.files.filter((f) => f.id !== fileId);
        const item = this.container.querySelector(`[data-file-id="${fileId}"]`);
        if (item) {
            item.style.animation = 'slideUp 0.3s ease-out forwards';
            setTimeout(() => item.remove(), 300);
        }

        if (this.files.length === 0) {
            const list = this.container.querySelector('.file-upload-list');
            if (list) list.remove();
        }
    }

    updateProgress(fileId, progress) {
        const item = this.container.querySelector(`[data-file-id="${fileId}"]`);
        if (item) {
            const progressBar = item.querySelector('.progress-bar');
            progressBar.style.width = `${progress}%`;
        }
    }

    getFileIcon(type) {
        if (type.includes('image')) return '🖼️';
        if (type.includes('pdf')) return '📄';
        if (type.includes('video')) return '🎥';
        if (type.includes('audio')) return '🎵';
        return '📎';
    }

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }

    showError(message) {
        let alertContainer = this.container.querySelector('.form-alert');

        if (!alertContainer) {
            alertContainer = document.createElement('div');
            alertContainer.className = 'form-alert form-alert--error';
            this.container.insertBefore(alertContainer, this.container.firstChild);
        }

        alertContainer.innerHTML = `
      <div class="form-alert-title">Fehler</div>
      <div class="form-alert-message">${message}</div>
    `;

        setTimeout(() => alertContainer.remove(), 4000);
    }

    getFiles() {
        return this.files.map((f) => f.file);
    }
}

export default FileUploadManager;
